import { EditOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { TableColumnsType, Tag } from 'antd'
import { SelectProps } from 'antd/lib'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  useIdentityServiceDeleteProfileEndpoint,
  useIdentityServiceGetProfilesListEndpointKey
} from 'src/api/queries'
import { PersonStatus } from 'src/api/requests'
import { Paragraph, TableActionButtons, DeleteConfirm } from 'src/components'
import { useDefaultSortingItem } from 'src/hooks'
import { dayMonthYearBackSlash, formatDate } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'

export const useConfig = (isAdmin: boolean) => {
  const { t } = useLocales()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { defaultSortingOption } = useDefaultSortingItem()

  const { mutate, isPending: isDeleting } =
    useIdentityServiceDeleteProfileEndpoint({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [useIdentityServiceGetProfilesListEndpointKey]
        })
      }
    })

  const orderingOptions: SelectProps['options'] = [
    defaultSortingOption,
    {
      value: 'LastName',
      label: t('users.name_asc')
    },
    {
      value: '-LastName',
      label: t('users.name_desc')
    },
    {
      value: 'DateOfBirth',
      label: t('users.birth_date_asc')
    },
    {
      value: '-DateOfBirth',
      label: t('users.birth_date_desc')
    }
  ]

  const getActionItems = useMemo(
    () => (id: string) => {
      return [
        {
          key: 'edit',

          label: t('common.edit'),
          icon: <EditOutlined />,
          onClick: () => navigate(paths.users.edit(String(id)))
        },
        ...(isAdmin
          ? [
              {
                key: 'delete',
                label: (
                  <DeleteConfirm
                    title={t('users.delete_user_title')}
                    onConfirm={() => mutate({ id })}
                  />
                ),
                danger: true
              }
            ]
          : [])
      ]
    },
    [t]
  )

  const authenticationTypes: SelectProps['options'] = [
    {
      key: 'email',
      label: t('users.authentication_type_email'),
      value: 0
    },
    {
      key: 'mpass',
      label: t('users.authentication_type_mpass'),
      value: 1
    }
  ]

  const columns = useMemo(
    (): TableColumnsType => [
      {
        key: 'idnp',
        title: t('form.idnp'),
        dataIndex: 'idnp'
      },
      {
        key: 'lastName',
        title: t('form.last_name'),
        dataIndex: 'lastName'
      },
      {
        key: 'firstName',
        title: t('form.first_name'),
        dataIndex: 'firstName'
      },
      {
        key: 'email',
        title: t('form.email'),
        dataIndex: 'email'
      },
      {
        key: 'birth date',
        title: t('form.birth_date'),
        render: ({ dateOfBirth }) =>
          formatDate(dateOfBirth, dayMonthYearBackSlash)
      },
      {
        key: 'roles',
        title: t('users.system_role'),
        render: ({ roles }) => (
          <>
            {roles.length ? (
              <Paragraph weight={400} ellipsis={{ rows: 2 }}>
                {roles?.map((item: string) => (
                  <div key={item}>{item}</div>
                ))}
              </Paragraph>
            ) : (
              <>-</>
            )}
          </>
        )
      },
      {
        key: 'status',
        title: t('form.status'),
        render: ({ status }) => {
          const isActive = status === PersonStatus.ACTIVE

          return (
            <Tag color={isActive ? 'success' : 'error'}>
              {isActive ? t('common.active') : t('common.inactive')}
            </Tag>
          )
        }
      },
      {
        key: 'actions',
        fixed: 'right',
        render: ({ id }) => <TableActionButtons items={getActionItems(id)} />
      }
    ],
    [t]
  )

  return { columns, isDeleting, orderingOptions, authenticationTypes }
}
